import { type InstalledPlugin, isUserPluginSource } from '@crowdex/plugin-contracts';
import { Input } from '@crowdex/ui/components/input';
import { Folder, Link2, LoaderCircle, MessageSquareText, Package } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { settingButtonClass } from '@/components/settings-page/setting-controls';
import { openNewConversation } from '@/features/apps/app-conversation';
import { isElectronDesktop } from '@/lib/electron-bridge';
import type { usePluginManagement } from '@/lib/plugins-page/use-plugin-management';
import { InstalledPluginRow } from './plugin-card';
import { GrantList } from './plugin-grants';

type Management = ReturnType<typeof usePluginManagement>;

/** What the agent is asked to add an MCP server as a plugin the user installs from its card. */
export function mcpServerRequest(server: string): string {
  return `Add the MCP server ${server} as a plugin of this workspace, following the plugin-authoring skill, so I can install it from its card.`;
}

function Card({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col rounded-2xl border bg-card p-4">
      <h2 className="flex items-center gap-2.5 font-semibold text-sm">
        {icon}
        {title}
      </h2>
      {children}
    </section>
  );
}

/**
 * Plugins › Custom (D31b): import a folder of this computer, add an MCP server or write a skill
 * with the agent; then the user's own plugins, with their switch.
 */
export function CustomPlugins({
  management,
  phone,
  accountCount,
  onOpen,
  onOpenMarketplaces,
}: {
  management: Management;
  phone: boolean;
  accountCount: (plugin: InstalledPlugin) => number;
  onOpen: (plugin: InstalledPlugin) => void;
  /** Marketplaces (D33b): a computer opens them from the page's header, a phone from here. */
  onOpenMarketplaces?: () => void;
}) {
  const [path, setPath] = useState('');
  const [server, setServer] = useState('');
  const yours = management.listed.filter((plugin) => isUserPluginSource(plugin.source));
  const pending = management.pendingImport;
  const importable = management.desktop;
  return (
    <>
      <div className="mb-7 grid gap-3 md:grid-cols-3">
        {!phone && (
          <Card icon={<Folder aria-hidden className="size-4" />} title="Import a local folder">
            <p className="mt-2 mb-3 text-muted-foreground text-sm leading-5">
              A folder with a <code className="font-mono text-foreground">crowdex.json</code>, a{' '}
              <code className="font-mono text-foreground">SKILL.md</code> or an{' '}
              <code className="font-mono text-foreground">mcp.json</code>. Desktop only.
            </p>
            <form
              className="mt-auto flex gap-2"
              onSubmit={(event) => {
                event.preventDefault();
                void management.importLocal(path);
              }}
            >
              <Input
                aria-label="Folder to import"
                className="h-9 min-w-0 flex-1 rounded-lg bg-control px-3 font-mono text-foreground text-xs outline-none placeholder:text-muted-foreground disabled:opacity-60"
                disabled={!importable}
                onChange={(event) => setPath(event.target.value)}
                placeholder="~/code/my-plugin"
                value={path}
              />
              <button
                className={settingButtonClass()}
                disabled={
                  !importable ||
                  management.busyPlugin !== null ||
                  (path.trim() === '' && !isElectronDesktop())
                }
                type="submit"
              >
                {management.busyPlugin !== null && path !== '' ? (
                  <LoaderCircle className="size-3.5 animate-spin" />
                ) : null}
                {path.trim() === '' && isElectronDesktop() ? 'Choose…' : 'Import'}
              </button>
            </form>
          </Card>
        )}
        <Card icon={<Link2 aria-hidden className="size-4" />} title="Add an MCP server">
          <p className="mt-2 mb-3 text-muted-foreground text-sm leading-5">
            A URL or a command; the agent makes it a plugin you install, available in every
            conversation.
          </p>
          <form
            className="mt-auto flex gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              if (server.trim() !== '') openNewConversation(mcpServerRequest(server.trim()));
            }}
          >
            <Input
              aria-label="MCP server URL or command"
              className="h-9 min-w-0 flex-1 rounded-lg bg-control px-3 font-mono text-foreground text-xs outline-none placeholder:text-muted-foreground"
              onChange={(event) => setServer(event.target.value)}
              placeholder="https://…/mcp"
              value={server}
            />
            <button className={settingButtonClass()} disabled={server.trim() === ''} type="submit">
              Add
            </button>
          </form>
        </Card>
        <Card
          icon={<MessageSquareText aria-hidden className="size-4" />}
          title="Write a skill with the agent"
        >
          <p className="mt-2 mb-3 text-muted-foreground text-sm leading-5">
            Describe what it should do; a conversation builds and tests it.
          </p>
          <button
            className={`${settingButtonClass()} mt-auto w-full`}
            onClick={() => openNewConversation()}
            type="button"
          >
            Start
          </button>
        </Card>
        {phone && onOpenMarketplaces !== undefined && (
          <Card icon={<Package aria-hidden className="size-4" />} title="Marketplaces">
            <p className="mt-2 mb-3 text-muted-foreground text-sm leading-5">
              A GitHub repository with a Claude Code or Codex marketplace: its plugins join the
              catalog.
            </p>
            <button
              className={`${settingButtonClass()} mt-auto w-full`}
              onClick={onOpenMarketplaces}
              type="button"
            >
              Marketplaces
            </button>
          </Card>
        )}
      </div>
      {pending && (
        <div className="mb-7 space-y-3 rounded-2xl border bg-card p-4">
          <p className="text-sm">
            {pending.preview.title} {pending.preview.version} may:
          </p>
          <GrantList grants={pending.preview.grants} label="What it may do" />
          <div className="flex gap-2">
            <button
              className={settingButtonClass('primary')}
              disabled={management.busyPlugin !== null}
              onClick={() => void management.confirmImport()}
              type="button"
            >
              Install
            </button>
            <button
              className={settingButtonClass()}
              onClick={management.cancelImport}
              type="button"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      <p className="mb-2 px-1 text-muted-foreground text-sm">Yours · {yours.length}</p>
      {yours.length === 0 ? (
        <p className="rounded-2xl border border-hairline border-dashed py-10 text-center text-muted-foreground text-sm">
          No plugins, MCP servers or skills of your own yet.
        </p>
      ) : (
        <div className="overflow-hidden rounded-2xl border bg-card">
          {yours.map((plugin) => (
            <InstalledPluginRow
              accounts={accountCount(plugin)}
              busy={management.busyPlugin === plugin.name}
              detail={[plugin.discoveryProvider ?? 'Local', plugin.localSourcePath]
                .filter(Boolean)
                .join(' · ')}
              key={plugin.name}
              onOpen={onOpen}
              onRefresh={
                plugin.localSourcePath !== undefined && management.desktop
                  ? () => void management.refreshLocal(plugin)
                  : undefined
              }
              onToggle={management.toggle}
              phone={phone}
              plugin={plugin}
            />
          ))}
        </div>
      )}
      {phone && (
        <p className="mt-3 px-1 text-muted-foreground text-xs">
          Importing a local folder is only on a computer.
        </p>
      )}
    </>
  );
}
